import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    doc,
    getDoc,
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    setDoc,
    getDocs,
    query,
    orderBy,
    where
} from 'firebase/firestore';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { db } from '../firebase';
import { stripHtmlTags, isAdmin } from '../utils';

export function AdminPage({
    user,
    memorizationTexts = [],
    memorizationSettings = {},
    navigateToPage
}) {
    const [newBibleText, setNewBibleText] = useState({ passage: '', text: '', category: '' });
    const [newChurchHistory, setNewChurchHistory] = useState('');
    const [adminLoading, setAdminLoading] = useState(false);
    const [adminMessage, setAdminMessage] = useState('');
    const [activeTab, setActiveTab] = useState('bible'); // New state for active tab
    const [selectedAdminYear, setSelectedAdminYear] = useState(new Date().getFullYear());
    const [yearVisibility, setYearVisibility] = useState({}); // Track which years are visible to users
    const [isBatchMode, setIsBatchMode] = useState(false);
    const [batchInput, setBatchInput] = useState('');
    const [batchLogs, setBatchLogs] = useState([]);
    const [showVisibilitySettings, setShowVisibilitySettings] = useState(false);

    // Bible search state for admin
    const [adminBibleQuery, setAdminBibleQuery] = useState('');
    const [adminSearchResults, setAdminSearchResults] = useState([]);
    const [adminIsSearching, setAdminIsSearching] = useState(false);
    const adminSearchTimeoutRef = useRef(null);

    // User management state
    const [allUsers, setAllUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [userSearch, setUserSearch] = useState('');
    const [userFilter, setUserFilter] = useState('all'); // 'all', 'active', 'inactive', 'admin'
    const [selectedUser, setSelectedUser] = useState(null);
    const [showUserModal, setShowUserModal] = useState(false);
    const [userModalMode, setUserModalMode] = useState('view'); // 'view', 'edit', 'delete'
    const [userLoading, setUserLoading] = useState(false);

    // Quill editor configuration for admin
    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'align': [] }],
            ['link'],
            ['clean']
        ]
    };

    // Debug authentication and admin status
    useEffect(() => {
        console.log('🔍 Admin Debug: user:', user);
        console.log('🔍 Admin Debug: user?.uid:', user?.uid);
        console.log('🔍 Admin Debug: isAdmin():', isAdmin(user));
        console.log('🔍 Admin Debug: REACT_APP_ADMIN_USER_ID:', process.env.REACT_APP_ADMIN_USER_ID);
        console.log('🔍 Admin Debug: db:', db);
    }, [user]);

    // Admin Bible search functionality
    const searchBiblePassageAdmin = useCallback(async (query) => {
        if (!query.trim()) {
            setAdminSearchResults([]);
            return;
        }

        setAdminIsSearching(true);

        // Clear previous timeout
        if (adminSearchTimeoutRef.current) {
            clearTimeout(adminSearchTimeoutRef.current);
        }

        // Debounce the search
        adminSearchTimeoutRef.current = setTimeout(async () => {
            try {
                // Using KJV (King James Version) translation
                const response = await fetch(`https://bible-api.com/${encodeURIComponent(query)}?translation=kjv`);

                if (!response.ok) {
                    throw new Error('Failed to fetch Bible passage');
                }

                const data = await response.json();

                if (data.error) {
                    setAdminSearchResults([]);
                } else {
                    // Format the results for admin display
                    const results = [];

                    if (data.verses && data.verses.length > 0) {
                        // Multiple verses
                        results.push({
                            reference: data.reference,
                            text: data.text,
                            verses: data.verses
                        });
                    } else if (data.text) {
                        // Single passage
                        results.push({
                            reference: data.reference,
                            text: data.text
                        });
                    }

                    setAdminSearchResults(results);
                }
            } catch (error) {
                console.error('Error searching Bible passage:', error);
                setAdminSearchResults([]);
            } finally {
                setAdminIsSearching(false);
            }
        }, 300);
    }, []);

    // Handle selecting a search result in admin
    const selectAdminSearchResult = (result) => {
        setNewBibleText(prev => ({
            ...prev,
            passage: result.reference,
            text: result.text
        }));
        setAdminBibleQuery('');
        setAdminSearchResults([]);
    };

    // Cleanup admin search timeout on unmount
    useEffect(() => {
        return () => {
            if (adminSearchTimeoutRef.current) {
                clearTimeout(adminSearchTimeoutRef.current);
            }
        };
    }, []);

    const addBibleText = async () => {
        console.log('🔍 Debug: addBibleText called');
        console.log('🔍 Debug: newBibleText:', newBibleText);
        console.log('🔍 Debug: user:', user);

        if (!newBibleText.passage || !newBibleText.text) {
            console.log('🚨 Debug: Missing required fields');
            setAdminMessage('Please fill in all required fields');
            return;
        }

        try {
            setAdminLoading(true);
            console.log('🔍 Debug: Starting database operation...');

            const appId = process.env.REACT_APP_MASTER_GUIDE_APP_ID || window.__app_id || 'masterguide-2a719';
            console.log('🔍 Debug: Using appId:', appId);

            const memTextsRef = collection(db, `artifacts/${appId}/public/data/memorizationTexts`);
            console.log('🔍 Debug: Collection reference created');

            const docData = {
                ...newBibleText,
                year: parseInt(selectedAdminYear),
                createdAt: new Date(),
                createdBy: user ? user.uid : 'anonymous'
            };
            console.log('🔍 Debug: Document data to save:', docData);

            const docRef = await addDoc(memTextsRef, docData);
            console.log('🔍 Debug: Document added with ID:', docRef.id);

            setNewBibleText({ passage: '', text: '', category: '' });
            setAdminMessage('Bible text added successfully!');

            setTimeout(() => setAdminMessage(''), 3000);
        } catch (error) {
            console.error('🚨 Error adding Bible text:', error);
            console.error('🚨 Error details:', error.message);
            console.error('🚨 Error code:', error.code);
        } finally {
            setAdminLoading(false);
        }
    };

    const handleToggleYearVisibility = async (year) => {
        try {
            const appId = process.env.REACT_APP_MASTER_GUIDE_APP_ID || window.__app_id || 'masterguide-2a719';
            const settingsRef = doc(db, `artifacts/${appId}/public/data/settings`, 'memorization');

            // Toggle logic
            const currentActive = memorizationSettings.activeYears || [];
            const newActiveYears = currentActive.includes(year)
                ? currentActive.filter(y => y !== year)
                : [...currentActive, year];

            await setDoc(settingsRef, { activeYears: newActiveYears }, { merge: true });
        } catch (error) {
            console.error('Error toggling year visibility:', error);
            setAdminMessage('Failed to update visibility settings');
        }
    };

    const handleBatchProcess = async () => {
        if (!batchInput.trim()) return;
        setAdminLoading(true);
        setBatchLogs([]);
        const lines = batchInput.split('\n').filter(l => l.trim());
        const newLogs = [];

        for (const line of lines) {
            const ref = line.trim();
            try {
                // Fetch KJV text
                const response = await fetch(`https://bible-api.com/${encodeURIComponent(ref)}?translation=kjv`);
                if (!response.ok) throw new Error('Fetch failed');
                const data = await response.json();
                if (data.error) throw new Error(data.error);

                const text = data.text || (data.verses && data.verses.map(v => v.text).join(' '));
                if (!text) throw new Error('No text found');

                // Add to Firestore
                const appId = process.env.REACT_APP_MASTER_GUIDE_APP_ID || window.__app_id || 'masterguide-2a719';
                const memTextsRef = collection(db, `artifacts/${appId}/public/data/memorizationTexts`);
                await addDoc(memTextsRef, {
                    passage: data.reference,
                    text: text.trim(),
                    category: 'memory', // Default category
                    year: parseInt(selectedAdminYear),
                    createdAt: new Date(),
                    createdBy: user ? user.uid : 'anonymous'
                });
                newLogs.push({ status: 'success', message: `✅ Added: ${data.reference}` });
            } catch (err) {
                newLogs.push({ status: 'error', message: `❌ Failed (${ref}): ${err.message}` });
            }
            setBatchLogs([...newLogs]);
        }
        setAdminLoading(false);
        if (newLogs.every(l => l.status === 'success')) {
            setBatchInput('');
            setAdminMessage('Batch processing complete!');
            setTimeout(() => setAdminMessage(''), 3000);
        }
    };

    const updateChurchHistory = async () => {
        if (!newChurchHistory.trim() || stripHtmlTags(newChurchHistory).trim().length === 0) {
            setAdminMessage('Please enter church history content');
            return;
        }

        try {
            setAdminLoading(true);
            const appId = process.env.REACT_APP_MASTER_GUIDE_APP_ID || window.__app_id || 'masterguide-2a719';
            const historyRef = doc(db, `artifacts/${appId}/public/data/churchHistory`, 'history');
            await setDoc(historyRef, {
                content: newChurchHistory,
                updatedAt: new Date(),
                updatedBy: user.uid
            });

            setAdminMessage('Church history updated successfully!');
            setTimeout(() => setAdminMessage(''), 3000);
        } catch (error) {
            console.error('Error updating church history:', error);
            setAdminMessage('Failed to update church history');
        } finally {
            setAdminLoading(false);
        }
    };

    const deleteBibleText = async (textId) => {
        if (!window.confirm('Are you sure you want to delete this Bible text?')) return;

        try {
            setAdminLoading(true);
            const appId = process.env.REACT_APP_MASTER_GUIDE_APP_ID || window.__app_id || 'masterguide-2a719';
            const docRef = doc(db, `artifacts/${appId}/public/data/memorizationTexts`, textId);
            await deleteDoc(docRef);
            setAdminMessage('Bible text deleted successfully!');
            setTimeout(() => setAdminMessage(''), 3000);
        } catch (error) {
            console.error('Error deleting Bible text:', error);
            setAdminMessage('Failed to delete Bible text');
        } finally {
            setAdminLoading(false);
        }
    };

    // ========================
    // USER MANAGEMENT FUNCTIONS
    // ========================

    // Fetch all users
    const fetchAllUsers = async () => {
        try {
            setUserLoading(true);
            const usersRef = collection(db, 'users');
            const snapshot = await getDocs(usersRef);
            const users = [];

            snapshot.forEach((doc) => {
                const userData = doc.data();
                users.push({
                    id: doc.id,
                    ...userData,
                    lastLogin: userData.lastLogin?.toDate() || null,
                    createdAt: userData.createdAt?.toDate() || null,
                    updatedAt: userData.updatedAt?.toDate() || null
                });
            });

            // Sort by most recent activity
            users.sort((a, b) => {
                const aDate = a.lastLogin || a.createdAt || new Date(0);
                const bDate = b.lastLogin || b.createdAt || new Date(0);
                return bDate - aDate;
            });

            setAllUsers(users);
            setFilteredUsers(users);
        } catch (error) {
            console.error('Error fetching users:', error);
            setAdminMessage('Failed to fetch users');
        } finally {
            setUserLoading(false);
        }
    };

    // Filter users based on search and filter criteria
    const filterUsers = (searchTerm, filterType) => {
        let filtered = allUsers;

        // Apply text search
        if (searchTerm.trim()) {
            filtered = filtered.filter(user =>
                user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.church?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.island?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Apply filter type
        switch (filterType) {
            case 'active':
                filtered = filtered.filter(user => {
                    const lastActivity = user.lastLogin || user.createdAt;
                    if (!lastActivity) return false;
                    const daysDiff = (new Date() - lastActivity) / (1000 * 60 * 60 * 24);
                    return daysDiff <= 30; // Active in last 30 days
                });
                break;
            case 'inactive':
                filtered = filtered.filter(user => {
                    const lastActivity = user.lastLogin || user.createdAt;
                    if (!lastActivity) return true;
                    const daysDiff = (new Date() - lastActivity) / (1000 * 60 * 60 * 24);
                    return daysDiff > 30; // Inactive for more than 30 days
                });
                break;
            case 'admin':
                const adminUserId = process.env.REACT_APP_ADMIN_USER_ID || 'adminUser123';
                filtered = filtered.filter(user => user.id === adminUserId);
                break;
            default:
                // 'all' - no additional filtering
                break;
        }

        setFilteredUsers(filtered);
    };

    // Handle search input change
    const handleUserSearch = (searchTerm) => {
        setUserSearch(searchTerm);
        filterUsers(searchTerm, userFilter);
    };

    // Handle filter change
    const handleUserFilterChange = (filterType) => {
        setUserFilter(filterType);
        filterUsers(userSearch, filterType);
    };

    // Open user modal
    const openUserModal = (user, mode = 'view') => {
        setSelectedUser(user);
        setUserModalMode(mode);
        setShowUserModal(true);
    };

    // Close user modal
    const closeUserModal = () => {
        setShowUserModal(false);
        setSelectedUser(null);
        setUserModalMode('view');
    };

    // Update user data
    const updateUser = async (userId, updateData) => {
        try {
            setUserLoading(true);
            const userRef = doc(db, 'users', userId);
            await updateDoc(userRef, {
                ...updateData,
                updatedAt: new Date(),
                updatedBy: user.uid
            });

            setAdminMessage('User updated successfully!');
            setTimeout(() => setAdminMessage(''), 3000);

            // Refresh user list
            fetchAllUsers();
            closeUserModal();
        } catch (error) {
            console.error('Error updating user:', error);
            setAdminMessage('Failed to update user');
        } finally {
            setUserLoading(false);
        }
    };

    // Delete user
    const deleteUser = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
            return;
        }

        try {
            setUserLoading(true);

            // Delete user document
            await deleteDoc(doc(db, 'users', userId));

            // TODO: Also clean up user's data (devotionals, progress, etc.)
            // This would require additional cleanup operations

            setAdminMessage('User deleted successfully!');
            setTimeout(() => setAdminMessage(''), 3000);

            // Refresh user list
            fetchAllUsers();
            closeUserModal();
        } catch (error) {
            console.error('Error deleting user:', error);
            setAdminMessage('Failed to delete user');
        } finally {
            setUserLoading(false);
        }
    };

    // Export user data to CSV
    const exportUsersToCSV = () => {
        const csvContent = [
            ['Name', 'Email', 'Church', 'Island', 'Created At', 'Last Login', 'Status'].join(','),
            ...filteredUsers.map(user => [
                user.name || 'N/A',
                user.email || 'N/A',
                user.church || 'N/A',
                user.island || 'N/A',
                user.createdAt ? user.createdAt.toLocaleDateString() : 'N/A',
                user.lastLogin ? user.lastLogin.toLocaleDateString() : 'Never',
                getActivityStatus(user)
            ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `master-guide-users-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    // Get user activity status
    const getActivityStatus = (user) => {
        const lastActivity = user.lastLogin || user.createdAt;
        if (!lastActivity) return 'Unknown';

        const daysDiff = (new Date() - lastActivity) / (1000 * 60 * 60 * 24);
        if (daysDiff <= 7) return 'Very Active';
        if (daysDiff <= 30) return 'Active';
        if (daysDiff <= 90) return 'Inactive';
        return 'Very Inactive';
    };

    // Load users when users tab is selected
    useEffect(() => {
        if (activeTab === 'users' && allUsers.length === 0) {
            fetchAllUsers();
        }
    }, [activeTab]);

    return (
        <div className="max-w-4xl mx-auto">
            <div className="flex items-center mb-8">
                <button
                    onClick={() => navigateToPage('home')}
                    className="mr-4 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Back to Home"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                </button>
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-8 rounded-xl shadow-lg mb-8 text-white relative overflow-hidden">
                    <div className="relative z-10">
                        <h2 className="text-4xl font-extrabold tracking-tight mb-2">Admin Dashboard</h2>
                        <p className="text-indigo-100 text-lg max-w-2xl">
                            Manage content, users, and settings for the Master Guide application.
                        </p>
                    </div>
                    <div className="absolute top-0 right-0 p-8 opacity-10 transform translate-x-12 -translate-y-6">
                        <span className="text-9xl">⚙️</span>
                    </div>
                </div>
            </div>

            {
                adminMessage && (
                    <div className={`fixed top-4 right-4 z-[100] p-4 rounded-lg shadow-xl max-w-sm animate-bounce-in flex items-center justify-between border-l-4 ${adminMessage.includes('success')
                        ? 'bg-green-50 text-green-800 border-green-500'
                        : 'bg-red-50 text-red-800 border-red-500'
                        }`}>
                        <div className="flex items-center">
                            <span className="mr-3 text-xl">
                                {adminMessage.includes('success') ? '✅' : '⚠️'}
                            </span>
                            <span className="font-medium">{adminMessage}</span>
                        </div>
                        <button
                            onClick={() => setAdminMessage('')}
                            className="ml-4 text-gray-400 hover:text-gray-600 focus:outline-none"
                            title="Close"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                )
            }

            {/* Admin Dashboard Tabs */}
            <div className="bg-white rounded-lg shadow-sm mb-8">
                {/* Tab Navigation */}
                <div className="border-b border-gray-200">
                    <nav className="flex space-x-8 px-6">
                        <button
                            onClick={() => setActiveTab('bible')}
                            className={`py-4 px-6 border-b-2 font-medium text-sm transition-all duration-200 flex flex-col items-center gap-1 ${activeTab === 'bible'
                                ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                }`}
                        >
                            <span className="text-2xl">📖</span>
                            <span>Bible Texts</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('history')}
                            className={`py-4 px-6 border-b-2 font-medium text-sm transition-all duration-200 flex flex-col items-center gap-1 ${activeTab === 'history'
                                ? 'border-purple-600 text-purple-700 bg-purple-50/50'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                }`}
                        >
                            <span className="text-2xl">🏛️</span>
                            <span>History</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('overview')}
                            className={`py-4 px-6 border-b-2 font-medium text-sm transition-all duration-200 flex flex-col items-center gap-1 ${activeTab === 'overview'
                                ? 'border-green-600 text-green-700 bg-green-50/50'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                }`}
                        >
                            <span className="text-2xl">📊</span>
                            <span>Data</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('users')}
                            className={`py-4 px-6 border-b-2 font-medium text-sm transition-all duration-200 flex flex-col items-center gap-1 ${activeTab === 'users'
                                ? 'border-orange-600 text-orange-700 bg-orange-50/50'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                }`}
                        >
                            <span className="text-2xl">👥</span>
                            <span>Users</span>
                        </button>
                    </nav>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                    {/* Bible Text Management Tab */}
                    {activeTab === 'bible' && (
                        <div>
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl font-semibold text-gray-800">Bible Text Management</h3>
                                <div className="flex items-center space-x-4">
                                    <div className="flex items-center space-x-2 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100">
                                        <label className="text-sm font-medium text-blue-800">Target Year:</label>
                                        <select
                                            value={selectedAdminYear}
                                            onChange={(e) => setSelectedAdminYear(parseInt(e.target.value))}
                                            className="bg-white border border-blue-200 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-blue-900 font-medium"
                                        >
                                            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() + 1 - i).map(year => (
                                                <option key={year} value={year}>{year}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <button
                                        onClick={() => setShowVisibilitySettings(!showVisibilitySettings)}
                                        className={`p-2 rounded-lg border transition-colors ${showVisibilitySettings ? 'bg-blue-100 border-blue-300 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                                        title="Manage Year Visibility"
                                    >
                                        <span className="text-lg">👁️</span>
                                    </button>
                                </div>
                            </div>

                            {showVisibilitySettings && (
                                <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                                    <h5 className="text-sm font-bold text-gray-700 mb-3">Visible Years to Students</h5>
                                    <div className="flex flex-wrap gap-2">
                                        {[...new Set(memorizationTexts.map(t => t.year || new Date().getFullYear()))]
                                            .sort((a, b) => b - a)
                                            .map(year => {
                                                const isActive = (memorizationSettings.activeYears || []).includes(year);
                                                return (
                                                    <button
                                                        key={year}
                                                        onClick={() => handleToggleYearVisibility(year)}
                                                        className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${isActive
                                                            ? 'bg-green-100 border-green-300 text-green-800 hover:bg-green-200'
                                                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                            }`}
                                                    >
                                                        {year} {isActive ? '✅' : '🚫'}
                                                    </button>
                                                );
                                            })}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">
                                        Only checked years will be shown in the Study Resources section.
                                    </p>
                                </div>
                            )}

                            {/* Add New Text Form with Search */}
                            <div className="mb-8 bg-gray-50 p-6 rounded-lg border border-gray-200">
                                <h4 className="font-medium text-gray-900 mb-4">Add New Bible Text</h4>

                                {/* Bible Search Input */}
                                <div className="mb-4 relative">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Search Bible Passage (KJV)
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={adminBibleQuery}
                                            onChange={(e) => {
                                                setAdminBibleQuery(e.target.value);
                                                searchBiblePassageAdmin(e.target.value);
                                            }}
                                            placeholder="e.g. John 3:16 or Ps 23"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent pr-10"
                                        />
                                        {adminIsSearching && (
                                            <div className="absolute right-3 top-2.5">
                                                <div className="animate-spin h-5 w-5 border-2 border-indigo-500 rounded-full border-t-transparent"></div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Search Results Dropdown */}
                                    {adminSearchResults.length > 0 && (
                                        <div className="absolute z-10 w-full mt-1 bg-white shadow-lg rounded-lg border border-gray-200 max-h-60 overflow-y-auto">
                                            {adminSearchResults.map((result, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => selectAdminSearchResult(result)}
                                                    className="w-full text-left px-4 py-3 hover:bg-indigo-50 border-b last:border-b-0 transition-colors"
                                                >
                                                    <div className="font-bold text-gray-800">{result.reference}</div>
                                                    <div className="text-sm text-gray-600 truncate">{result.text}</div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                    <input
                                        type="text"
                                        placeholder="Reference (e.g. John 3:16) - Auto-filled from search"
                                        value={newBibleText.passage}
                                        onChange={(e) => setNewBibleText({ ...newBibleText, passage: e.target.value })}
                                        className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                    <select
                                        value={newBibleText.category}
                                        onChange={(e) => setNewBibleText({ ...newBibleText, category: e.target.value })}
                                        className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    >
                                        <option value="">Select Category</option>
                                        <option value="doctrine">Doctrine</option>
                                        <option value="prophecy">Prophecy</option>
                                        <option value="standard">Christian Standards</option>
                                        <option value="history">Church History</option>
                                        <option value="salvation">Salvation</option>
                                        <option value="prayer">Prayer</option>
                                        <option value="relationship">Relationships</option>
                                        <option value="service">Service</option>
                                    </select>
                                </div>
                                <textarea
                                    placeholder="Bible Text Content (will be auto-filled from search, or type manually)"
                                    value={newBibleText.text}
                                    onChange={(e) => setNewBibleText({ ...newBibleText, text: e.target.value })}
                                    className="w-full px-4 py-2 border rounded-lg mb-4 h-24 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                                <button
                                    onClick={addBibleText}
                                    disabled={adminLoading}
                                    className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition duration-300"
                                >
                                    {adminLoading ? 'Adding...' : 'Add Bible Text'}
                                </button>
                            </div>

                            {/* Batch Entry Mode Toggle */}
                            <div className="mb-6 flex justify-end">
                                <button
                                    onClick={() => setIsBatchMode(!isBatchMode)}
                                    className="text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center"
                                >
                                    {isBatchMode ? 'Switch to Single Entry' : 'Switch to Batch Entry'}
                                    <span className="ml-1">Creating Multiple Entries?</span>
                                </button>
                            </div>

                            {/* Batch Entry UI */}
                            {isBatchMode && (
                                <div className="mb-8 bg-indigo-50 p-6 rounded-lg border border-indigo-200">
                                    <h4 className="font-bold text-indigo-900 mb-2">Batch Entry (One Reference per Line)</h4>
                                    <p className="text-xs text-indigo-700 mb-3">
                                        Enter references like "John 3:16" or "Psalm 23". We'll fetch the KJV text for you automatically.
                                    </p>
                                    <textarea
                                        value={batchInput}
                                        onChange={(e) => setBatchInput(e.target.value)}
                                        placeholder="John 3:16&#10;Gen 1:1&#10;Rev 14:12"
                                        className="w-full px-4 py-3 border border-indigo-300 rounded-lg mb-4 h-32 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />

                                    {/* Batch Logs */}
                                    {batchLogs.length > 0 && (
                                        <div className="mb-4 bg-white p-3 rounded border border-gray-200 max-h-40 overflow-y-auto text-xs font-mono">
                                            {batchLogs.map((log, i) => (
                                                <div key={i} className={log.status === 'success' ? 'text-green-600' : 'text-red-600'}>
                                                    {log.message}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <button
                                        onClick={handleBatchProcess}
                                        disabled={adminLoading || !batchInput.trim()}
                                        className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition duration-300 shadow-sm"
                                    >
                                        {adminLoading ? 'Processing...' : 'Process Batch'}
                                    </button>
                                </div>
                            )}

                            <div>
                                <h4 className="font-medium text-gray-900 mb-4">
                                    Existing Texts ({memorizationTexts.length})
                                    {selectedAdminYear && <span className="text-gray-500 font-normal ml-2">(Showing all, filter by year in list needed)</span>}
                                </h4>
                                <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                                    {memorizationTexts
                                        .filter(t => !selectedAdminYear || t.year === parseInt(selectedAdminYear))
                                        .map((text) => (
                                            <div key={text.id} className="flex justify-between items-start p-4 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                                                <div>
                                                    <span className="inline-block px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs font-medium mb-2">
                                                        {text.category}
                                                    </span>
                                                    <h5 className="font-bold text-gray-900">{text.passage}</h5>
                                                    <p className="text-gray-600 text-sm mt-1 line-clamp-2">{text.text}</p>
                                                    <div className="mt-2 text-xs text-gray-400">ID: {text.id} • Year: {text.year || 'N/A'}</div>
                                                </div>
                                                <button
                                                    onClick={() => deleteBibleText(text.id)}
                                                    className="ml-4 text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-full transition-colors"
                                                    title="Delete"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        ))}
                                    {memorizationTexts.filter(t => !selectedAdminYear || t.year === parseInt(selectedAdminYear)).length === 0 && (
                                        <div className="text-center py-8 text-gray-500">
                                            No texts found for year {selectedAdminYear}. Add some above!
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Church History Management Tab */}
                    {activeTab === 'history' && (
                        <div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-6">Church History Management</h3>
                            <div className="mb-6">
                                <ReactQuill
                                    theme="snow"
                                    value={newChurchHistory}
                                    onChange={setNewChurchHistory}
                                    modules={quillModules}
                                    className="bg-white h-64 mb-12"
                                />
                                <button
                                    onClick={updateChurchHistory}
                                    disabled={adminLoading}
                                    className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 transition duration-300 mt-4"
                                >
                                    {adminLoading ? 'Updating...' : 'Update History'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Data Overview Tab */}
                    {activeTab === 'overview' && (
                        <div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-6">Data Overview</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
                                    <h4 className="font-bold text-blue-900 mb-2">Total Bible Texts</h4>
                                    <p className="text-3xl font-bold text-blue-600">{memorizationTexts.length}</p>
                                </div>
                                <div className="bg-purple-50 p-6 rounded-lg border border-purple-100">
                                    <h4 className="font-bold text-purple-900 mb-2">Church History Length</h4>
                                    <p className="text-3xl font-bold text-purple-600">{stripHtmlTags(newChurchHistory || '').length} chars</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* User Management Tab */}
                    {activeTab === 'users' && (
                        <div>
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl font-semibold text-gray-800">User Management</h3>
                                <button
                                    onClick={fetchAllUsers}
                                    className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                    title="Refresh List"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                </button>
                            </div>

                            <div className="flex flex-col md:flex-row gap-4 mb-6">
                                <div className="flex-1 relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="Search users..."
                                        value={userSearch}
                                        onChange={(e) => handleUserSearch(e.target.value)}
                                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <select
                                        value={userFilter}
                                        onChange={(e) => handleUserFilterChange(e.target.value)}
                                        className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-lg"
                                    >
                                        <option value="all">All Users</option>
                                        <option value="active">Active (30 days)</option>
                                        <option value="inactive">Inactive</option>
                                        <option value="admin">Admins</option>
                                    </select>
                                    <button
                                        onClick={exportUsersToCSV}
                                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        title="Export to CSV"
                                    >
                                        <span className="mr-2">📥</span>
                                        Export
                                    </button>
                                </div>
                            </div>

                            {userLoading ? (
                                <div className="flex justify-center items-center py-20">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                                </div>
                            ) : (
                                <div className="bg-white shadow overflow-hidden rounded-lg border border-gray-200">
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        User
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Location
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Last Active
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Status
                                                    </th>
                                                    <th scope="col" className="relative px-6 py-3">
                                                        <span className="sr-only">Actions</span>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {filteredUsers.length > 0 ? (
                                                    filteredUsers.map((u) => (
                                                        <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className="flex items-center">
                                                                    <div className="flex-shrink-0 h-10 w-10">
                                                                        <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-bold bg-gradient-to-br ${u.photoURL ? '' : 'from-indigo-400 to-purple-400'}`}>
                                                                            {u.photoURL ? (
                                                                                <img className="h-10 w-10 rounded-full" src={u.photoURL} alt="" />
                                                                            ) : (
                                                                                (u.name || u.email || '?').charAt(0).toUpperCase()
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <div className="ml-4">
                                                                        <div className="text-sm font-medium text-gray-900">
                                                                            {u.name || 'No Name'}
                                                                        </div>
                                                                        <div className="text-sm text-gray-500">
                                                                            {u.email}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className="text-sm text-gray-900">{u.church || 'N/A'}</div>
                                                                <div className="text-sm text-gray-500">{u.island || 'N/A'}</div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {u.lastLogin ? u.lastLogin.toLocaleDateString() : 'Never'}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getActivityStatus(u) === 'Very Active' ? 'bg-green-100 text-green-800' :
                                                                    getActivityStatus(u) === 'Active' ? 'bg-blue-100 text-blue-800' :
                                                                        getActivityStatus(u) === 'Inactive' ? 'bg-yellow-100 text-yellow-800' :
                                                                            'bg-gray-100 text-gray-800'
                                                                    }`}>
                                                                    {getActivityStatus(u)}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                                <div className="flex justify-end space-x-2">
                                                                    <button
                                                                        onClick={() => openUserModal(u, 'view')}
                                                                        className="text-indigo-600 hover:text-indigo-900"
                                                                        title="View Details"
                                                                    >
                                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                                        </svg>
                                                                    </button>
                                                                    <button
                                                                        onClick={() => openUserModal(u, 'edit')}
                                                                        className="text-blue-600 hover:text-blue-900"
                                                                        title="Edit User"
                                                                    >
                                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                        </svg>
                                                                    </button>
                                                                    <button
                                                                        onClick={() => openUserModal(u, 'delete')}
                                                                        className="text-red-600 hover:text-red-900"
                                                                        title="Delete User"
                                                                    >
                                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                        </svg>
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                                                            No users found matching your search.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 text-sm text-gray-500">
                                        Showing {filteredUsers.length} of {allUsers.length} users
                                    </div>
                                </div>
                            )}

                            {/* User Modal */}
                            {showUserModal && selectedUser && (
                                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[110] p-4">
                                    <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden transform transition-all">
                                        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                                            <h3 className="text-lg font-bold text-gray-900">
                                                {userModalMode === 'view' && 'User Details'}
                                                {userModalMode === 'edit' && 'Edit User'}
                                                {userModalMode === 'delete' && 'Delete User'}
                                            </h3>
                                            <button
                                                onClick={closeUserModal}
                                                className="text-gray-400 hover:text-gray-500"
                                            >
                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>

                                        <div className="p-6">
                                            {userModalMode === 'view' && (
                                                <div className="space-y-4">
                                                    <div className="flex items-center space-x-4 mb-6">
                                                        <div className={`h-16 w-16 rounded-full flex items-center justify-center text-white text-2xl font-bold bg-gradient-to-br ${selectedUser.photoURL ? '' : 'from-indigo-500 to-purple-500'}`}>
                                                            {selectedUser.photoURL ? (
                                                                <img className="h-16 w-16 rounded-full" src={selectedUser.photoURL} alt="" />
                                                            ) : (
                                                                (selectedUser.name || selectedUser.email || '?').charAt(0).toUpperCase()
                                                            )}
                                                        </div>
                                                        <div>
                                                            <h4 className="text-xl font-bold text-gray-900">{selectedUser.name || 'No Name'}</h4>
                                                            <p className="text-gray-600">{selectedUser.email}</p>
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div className="bg-gray-50 p-3 rounded-lg">
                                                            <div className="text-xs text-gray-500 uppercase font-semibold">Church</div>
                                                            <div className="text-gray-900 font-medium">{selectedUser.church || 'N/A'}</div>
                                                        </div>
                                                        <div className="bg-gray-50 p-3 rounded-lg">
                                                            <div className="text-xs text-gray-500 uppercase font-semibold">Island</div>
                                                            <div className="text-gray-900 font-medium">{selectedUser.island || 'N/A'}</div>
                                                        </div>
                                                        <div className="bg-gray-50 p-3 rounded-lg">
                                                            <div className="text-xs text-gray-500 uppercase font-semibold">Status</div>
                                                            <div className="text-gray-900 font-medium">{getActivityStatus(selectedUser)}</div>
                                                        </div>
                                                        <div className="bg-gray-50 p-3 rounded-lg">
                                                            <div className="text-xs text-gray-500 uppercase font-semibold">Last Login</div>
                                                            <div className="text-gray-900 font-medium">
                                                                {selectedUser.lastLogin ? selectedUser.lastLogin.toLocaleDateString() : 'Never'}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="mt-6">
                                                        <h5 className="font-semibold text-gray-800 mb-2">Progress Overview</h5>
                                                        <div className="bg-gray-100 rounded-full h-2 w-full mb-1">
                                                            <div
                                                                className="bg-indigo-600 h-2 rounded-full"
                                                                style={{
                                                                    width: `${Math.min(
                                                                        (Object.values(selectedUser.memorizationProgress || {}).filter(Boolean).length /
                                                                            Math.max(memorizationTexts.length, 1)) * 100,
                                                                        100
                                                                    )}%`
                                                                }}
                                                            ></div>
                                                        </div>
                                                        <div className="flex justify-between text-xs text-gray-500">
                                                            <span>Memorization</span>
                                                            <span>{Object.values(selectedUser.memorizationProgress || {}).filter(Boolean).length} / {memorizationTexts.length}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {userModalMode === 'edit' && (
                                                <form onSubmit={(e) => {
                                                    e.preventDefault();
                                                    const formData = new FormData(e.target);
                                                    updateUser(selectedUser.id, {
                                                        name: formData.get('name'),
                                                        church: formData.get('church'),
                                                        island: formData.get('island')
                                                    });
                                                }}>
                                                    <div className="space-y-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700">Name</label>
                                                            <input
                                                                type="text"
                                                                name="name"
                                                                defaultValue={selectedUser.name}
                                                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700">Church</label>
                                                            <input
                                                                type="text"
                                                                name="church"
                                                                defaultValue={selectedUser.church}
                                                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700">Island</label>
                                                            <input
                                                                type="text"
                                                                name="island"
                                                                defaultValue={selectedUser.island}
                                                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                            />
                                                        </div>
                                                        <div className="pt-4 flex justify-end space-x-3">
                                                            <button
                                                                type="button"
                                                                onClick={closeUserModal}
                                                                className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300"
                                                            >
                                                                Cancel
                                                            </button>
                                                            <button
                                                                type="submit"
                                                                disabled={userLoading}
                                                                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                                                            >
                                                                {userLoading ? 'Saving...' : 'Save Changes'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </form>
                                            )}

                                            {userModalMode === 'delete' && (
                                                <div className="text-center">
                                                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                                        <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                        </svg>
                                                    </div>
                                                    <h4 className="text-xl font-bold text-gray-900 mb-2">Confirm Deletion</h4>
                                                    <p className="text-gray-500 mb-6">
                                                        Are you sure you want to delete <strong>{selectedUser.name || selectedUser.email}</strong>?
                                                        This action cannot be undone and will remove all their data.
                                                    </p>
                                                    <div className="flex justify-center space-x-3">
                                                        <button
                                                            onClick={closeUserModal}
                                                            className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300"
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            onClick={() => deleteUser(selectedUser.id)}
                                                            disabled={userLoading}
                                                            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
                                                        >
                                                            {userLoading ? 'Deleting...' : 'Delete User'}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )
                    }
                </div>
            </div>
        </div>
    );
}
